import { useQueryClient } from "react-query";
import {
  BrandContentBriefQuery,
  ContentBriefMediaStatus,
  useCreateContentBriefDeliverableActivityMutation,
  useUpdateContentBriefMediaMutation,
} from "../../../graphql/generated";
import { queryKeys } from "../../../graphql/queryKeys";
import useGqlClient from "../../../hooks/useGqlClient";
import { aspectRatioToCSS } from "../../../utils/aspectRatio";
import { ActivityItem, MediaItem, MediaReviewDialog } from "../../MediaReview";
import { MediaReviewType } from "../../MediaReview/MediaReviewDialog";

interface DeliverableMediaReviewProps {
  deliverable: BrandContentBriefQuery["contentBrief"]["deliverables"][0];
  onReview: (approved: boolean) => void;
  isOpen: boolean;
  setIsOpen: (arg0: boolean) => void;
  contentBriefBookingId: string;
  onActivityUpdate?: () => void;
}

export const DeliverableMediaReview = (props: DeliverableMediaReviewProps) => {
  const client = useGqlClient();
  const queryClient = useQueryClient();

  const { mutate: createContentBriefDeliverableActivity } =
    useCreateContentBriefDeliverableActivityMutation(client, {
      onSuccess: () => {
        props.onActivityUpdate?.();
      },
      onError: (error: any) => {
        console.error("Error creating activity:", error);
      },
    });

  const { mutate: updateContentBriefMedia } =
    useUpdateContentBriefMediaMutation(client, {
      onSuccess: (response) => {
        queryClient.invalidateQueries(["BrandContentBrief"], {
          exact: false,
        });
        if (response.updateContentBriefMedia.bookingComplete) {
          props.onReview(true);
        } else {
          props.onReview(false);
        }
      },
      onError: (error: any) => {
        console.error("Error approving media:", error);
      },
    });

  const handleApprove = () => {
    if (!props.deliverable.media?.id) return;

    updateContentBriefMedia({
      input: {
        ID: props.deliverable.media.id,
        Status: ContentBriefMediaStatus.Approved,
      },
    });
  };

  const handleAddComment = (comment: string, timestamp?: number) => {
    if (!props.contentBriefBookingId || !props.deliverable.media?.id) return;

    createContentBriefDeliverableActivity(
      {
        input: {
          contentBriefDeliverableId: props.deliverable.id,
          comment,
          commentTimestampSeconds: timestamp,
          contentBriefBookingId: props.contentBriefBookingId,
          contentBriefMediaId: props.deliverable.media.id,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["BrandContentBrief"], {
            exact: false,
          });
          // Also call the parent's onActivityUpdate callback
          props.onActivityUpdate?.();
        },
        onError: (error) => {
          console.error("Error creating comment:", error);
        },
      }
    );
  };

  if (!props.deliverable.media) return null;

  // Transform data to match MediaReviewDialog interface
  const media: MediaItem = {
    id: props.deliverable.media.id,
    mediaUrl: props.deliverable.media.mediaUrl,
    mediaType: props.deliverable.media.mediaType,
    aspectRatio: aspectRatioToCSS(props.deliverable.aspectRatio),
  };

  const activities: ActivityItem[] = props.deliverable.activity || [];

  return (
    <MediaReviewDialog
      isOpen={props.isOpen}
      setIsOpen={props.setIsOpen}
      media={media}
      type={MediaReviewType.ContentBriefDeliverable}
      activities={activities}
      onApprove={handleApprove}
      onAddComment={handleAddComment}
      onActivityUpdate={() => {
        // Invalidate queries to refresh data
        queryClient.invalidateQueries([queryKeys.brandContentBrief], {
          exact: false,
        });
        queryClient.invalidateQueries([queryKeys.bookingPostSubmissions], {
          exact: false,
        });
        queryClient.invalidateQueries([queryKeys.getBookingPostSubmission], {
          exact: false,
        });
        // Call parent callback
        props.onActivityUpdate?.();
      }}
    />
  );
};
